import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import styles from './EmptyStates.module.css';

/**
 * EAA-1817 → EAA-1837: the empty/loading states the "Liên kết vai trò" table
 * needs. `NoStandardRolesEmptyState` and `RoleMapLoadingSkeleton` are carried
 * over from the coverage board (same cause, same copy); the "app not approved"
 * state went away with the per-app editor panel — a table has no selected app
 * to be unapproved. `RoleMapNoRowsEmptyState` and `RoleMapFilteredEmptyState`
 * are new and mirror `accounts/AccountsEmptyState.tsx`'s two-state split
 * (nothing here yet vs. nothing matches the filters).
 *
 * EAA-1804: the three CTAs below were hand-written `<button className="role-map-empty__cta">`,
 * an un-swapped instance of the kit's own dense primary `Button` spec — the CSS in
 * `styles/features/14-role-map.css` was height 34 / `var(--color-primary)` /
 * `var(--radius-md)` / `font-weight: 500`, token for token the kit's
 * `controlHeightSM`/primary look. Migrated to `<Button type="primary" size="small">`;
 * the old class and its CSS are removed since nothing else referenced it.
 */

export function NoStandardRolesEmptyState({ onCreateLevel }: { onCreateLevel: () => void }) {
    const { t } = useTranslation();
    return (
        <div className={styles.empty}>
            <span className={styles.emptyTitle}>
                {t('console.roleMap.emptyStates.noStandardRolesTitle')}
            </span>
            <p className={styles.emptyDesc}>
                {t('console.roleMap.emptyStates.noStandardRolesDesc')}
            </p>
            <Button type="primary" size="small" onClick={onCreateLevel}>
                {t('console.roleMap.emptyStates.createLevelCta')}
            </Button>
        </div>
    );
}

export function RoleMapNoRowsEmptyState({
    scopeLabel,
    canAdd,
    onAdd,
}: {
    scopeLabel: string;
    canAdd: boolean;
    onAdd: () => void;
}) {
    const { t } = useTranslation();
    return (
        <div className={styles.empty}>
            <span className={styles.emptyTitle}>
                {t('console.roleMap.emptyStates.noRowsTitle', { scopeLabel })}
            </span>
            <p className={styles.emptyDesc}>{t('console.roleMap.emptyStates.noRowsDesc')}</p>
            {canAdd && (
                <Button type="primary" size="small" onClick={onAdd}>
                    {t('console.roleMap.emptyStates.addLinkCta')}
                </Button>
            )}
        </div>
    );
}

export function RoleMapFilteredEmptyState({ onClear }: { onClear: () => void }) {
    const { t } = useTranslation();
    return (
        <div className={styles.empty}>
            <span className={styles.emptyTitle}>
                {t('console.roleMap.emptyStates.filteredTitle')}
            </span>
            <p className={styles.emptyDesc}>{t('console.roleMap.emptyStates.filteredDesc')}</p>
            <Button type="primary" size="small" onClick={onClear}>
                {t('console.roleMap.emptyStates.clearFiltersCta')}
            </Button>
        </div>
    );
}

export function RoleMapLoadingSkeleton() {
    const { t } = useTranslation();
    return (
        <div className={styles.skelCard} role="status" aria-live="polite">
            <span className="portal-sr-only">{t('console.roleMap.emptyStates.srLoadingText')}</span>
            <span className={styles.skelTitle} />
            {[0, 1, 2].map((row) => (
                <div className={styles.skelRow} key={row}>
                    <span className={`${styles.skelCell} ${styles.skelCellFirst}`} />
                    <span className={styles.skelCell} />
                    <span className={styles.skelCell} />
                    <span className={styles.skelCell} />
                    <span className={styles.skelCell} />
                </div>
            ))}
        </div>
    );
}
